(() => {
  if (window.FarazineIssue) { window.FarazineIssue.prepare(); return; }
  const instances = new Map();
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const narrow = window.matchMedia('(max-width: 760px)');

  function mount(root) {
    const stories = [...root.querySelectorAll('template[data-farazine-story]')];
    const coverIndex = Math.max(0, stories.findIndex(story => story.dataset.storyHandle === root.dataset.coverStory));
    const reader = root.querySelector('[data-farazine-reader]');
    const stage = root.querySelector('[data-farazine-stage]');
    const openButton = root.querySelector('[data-farazine-open]');
    if (!stories.length || !reader || !stage || !openButton) return null;
    const choices = [...root.querySelectorAll('[data-farazine-select]')];
    const previous = root.querySelector('[data-farazine-previous]');
    const next = root.querySelector('[data-farazine-next]');
    const counter = root.querySelector('[data-farazine-counter]');
    const number = root.querySelector('[data-farazine-number]');
    const status = root.querySelector('[data-farazine-status]');
    const resume = root.querySelector('[data-farazine-resume]');
    const sizeControls = [...root.querySelectorAll('[data-farazine-size]')];
    const edition = root.dataset.edition;
    const progress = window.FarazineProgress;
    const cleanups = [];
    let current = null, leaving = null, index = -1, turnTimer, saveTimer, restoreFrame, pointer = null, disposed = false, restoring = false;
    const frame = callback => window.requestAnimationFrame ? window.requestAnimationFrame(callback) : setTimeout(callback, 0);
    const cancelFrame = id => window.cancelAnimationFrame ? window.cancelAnimationFrame(id) : clearTimeout(id);

    function listen(target, type, callback) {
      target?.addEventListener(type, callback);
      cleanups.push(() => target?.removeEventListener(type, callback));
    }
    function stopPreview(container) {
      container?.querySelector('[data-fara-preview][aria-pressed="true"]')?.click();
    }
    function saved() {
      const record = progress?.read(edition);
      return record && stories.some(story => story.dataset.storyHandle === record.story) ? record : null;
    }
    function refreshResume() {
      if (!resume) return;
      const record = saved();
      resume.hidden = !record;
      if (!record) return;
      const title = stories.find(story => story.dataset.storyHandle === record.story).dataset.storyTitle;
      const label = root.dataset.resumeLabel;
      if (label) resume.setAttribute('aria-label', label.replace('__title__', title));
      const name = resume.querySelector('[data-farazine-resume-title]');
      if (name) name.textContent = title;
    }
    function applySize(size) {
      root.dataset.readingSize = size === 'large' ? 'large' : 'regular';
      sizeControls.forEach(button => {
        button.hidden = false;
        button.setAttribute('aria-pressed', String(button.dataset.farazineSize === root.dataset.readingSize));
      });
    }
    function pageScroller() {
      const wrapper = document.querySelector('.page-wrapper');
      return wrapper && window.getComputedStyle?.(wrapper).overflowY === 'auto' && wrapper.scrollHeight > wrapper.clientHeight ? wrapper : window;
    }
    function position() {
      const page = current?.querySelector('[data-farazine-read]');
      if (!page || index < 0) return null;
      let offset, maximum, mode = narrow.matches ? 'page' : 'panel';
      if (mode === 'page') {
        const scroller = pageScroller();
        const top = scroller === window ? 0 : scroller.getBoundingClientRect().top;
        const height = scroller === window ? window.innerHeight : scroller.clientHeight;
        offset = Math.max(0, top - page.getBoundingClientRect().top);
        maximum = Math.max(0, page.scrollHeight - height);
      } else {
        offset = page.scrollTop;
        maximum = Math.max(0, page.scrollHeight - page.clientHeight);
      }
      offset = Math.min(Math.max(0, Number(offset) || 0), maximum);
      return {story: stories[index].dataset.storyHandle, offset, position: maximum > 0 ? offset / maximum : 0,
        mode, size: root.dataset.readingSize, width: Number(window.innerWidth) || 0};
    }
    function saveProgress() {
      clearTimeout(saveTimer);
      if (restoring || !root.isConnected) return;
      const record = position();
      if (record) progress?.write(edition, record);
    }
    function cancelRestore() {
      cancelFrame(restoreFrame);
      restoring = false;
    }
    function restore(record) {
      cancelRestore();
      restoring = true;
      const target = current;
      restoreFrame = frame(() => {
        restoreFrame = frame(() => {
          if (disposed || !root.isConnected || current !== target) { restoring = false; return; }
          const page = target.querySelector('[data-farazine-read]');
          const mode = narrow.matches ? 'page' : 'panel';
          const scroller = pageScroller();
          const height = mode === 'panel' ? page.clientHeight : scroller === window ? window.innerHeight : scroller.clientHeight;
          const maximum = Math.max(0, page.scrollHeight - height);
          const unchanged = mode === record.mode && root.dataset.readingSize === record.size && Math.abs((Number(window.innerWidth) || 0) - record.width) < 2;
          const offset = Math.min(maximum, unchanged ? record.offset : record.position * maximum);
          if (mode === 'panel') {
            reader.scrollIntoView({block: 'start', behavior: 'instant'});
            page.scrollTop = offset;
          } else {
            const scrollTop = scroller === window ? window.scrollY || 0 : scroller.scrollTop;
            const top = scroller === window ? 0 : scroller.getBoundingClientRect().top;
            scroller.scrollTo({top: scrollTop + page.getBoundingClientRect().top - top + offset, behavior: 'instant'});
          }
          restoring = false;
          saveProgress();
        });
      });
    }
    function finishTurn() {
      clearTimeout(turnTimer);
      leaving?.remove();
      leaving = null;
      stage.setAttribute('aria-busy', 'false');
      previous?.removeAttribute('aria-disabled');
      next?.removeAttribute('aria-disabled');
    }
    function paint() {
      const label = (root.dataset.positionLabel || '')
        .replace('__current__', String(index + 1)).replace('__total__', String(stories.length))
        .replace('__title__', stories[index]?.dataset.storyTitle || '');
      if (counter) { counter.textContent = `${index + 1} / ${stories.length}`; counter.setAttribute('aria-label', label); }
      if (number) number.textContent = `${String(index * 2 + 2).padStart(2, '0')}—${String(index * 2 + 3).padStart(2, '0')}`;
      if (previous) previous.disabled = index <= 0;
      if (next) next.disabled = index >= stories.length - 1;
      choices.forEach(choice => {
        if (Number(choice.dataset.farazineSelect) === index) choice.setAttribute('aria-current', 'true');
        else choice.removeAttribute('aria-current');
      });
      if (status) status.textContent = label;
    }
    function reveal(container) {
      container.querySelectorAll('img[data-farazine-src],img[data-fara-src]').forEach(image => {
        const srcset = image.dataset.farazineSrcset || image.dataset.faraSrcset;
        const src = image.dataset.farazineSrc || image.dataset.faraSrc;
        image.loading = 'lazy';
        image.fetchPriority = 'auto';
        if (srcset) image.srcset = srcset;
        if (src) image.src = src;
        ['data-farazine-src', 'data-farazine-srcset', 'data-fara-src', 'data-fara-srcset'].forEach(name => image.removeAttribute(name));
      });
      container.querySelectorAll('.farazine__prose img').forEach(image => { image.loading = 'lazy'; image.fetchPriority = 'auto'; });
    }
    function remember() {
      const url = new URL(window.location.href);
      url.searchParams.set('story', stories[index].dataset.storyHandle);
      url.searchParams.delete('resume');
      url.hash = '';
      window.history.replaceState(window.history.state, '', url);
    }
    function select(position, {focus = false, updateUrl = true, restoreProgress = null, persist = true} = {}) {
      if (disposed || !root.isConnected || leaving || !Number.isInteger(position) || position < 0 || position >= stories.length) return;
      if (position === index && current) {
        if (restoreProgress) { restore(restoreProgress); return; }
        current.querySelector('[data-farazine-read]')?.focus({preventScroll: true});
        reader.scrollIntoView({block: 'start', behavior: 'instant'});
        return;
      }
      const fragment = stories[position].content.cloneNode(true);
      const incoming = fragment.querySelector('[data-farazine-spread]');
      if (!incoming) return;
      const outgoing = current;
      const previousIndex = index;
      const hadReadingFocus = outgoing?.contains(document.activeElement);
      saveProgress();
      cancelRestore();
      stopPreview(outgoing);
      index = position;
      current = incoming;
      reader.hidden = false;
      root.classList.add('is-open');
      reveal(incoming);
      stage.append(fragment);
      document.dispatchEvent(new Event('fara:preview-prepare'));
      paint();
      if (updateUrl) remember();
      if (outgoing && !motion.matches) {
        leaving = outgoing;
        outgoing.inert = true;
        outgoing.setAttribute('aria-hidden', 'true');
        outgoing.classList.add('is-leaving', index > previousIndex ? 'is-turning-forward' : 'is-turning-backward');
        stage.setAttribute('aria-busy', 'true');
        previous?.setAttribute('aria-disabled', 'true');
        next?.setAttribute('aria-disabled', 'true');
        turnTimer = setTimeout(finishTurn, 560);
      } else outgoing?.remove();
      if (focus || hadReadingFocus) {
        incoming.querySelector('[data-farazine-read]')?.focus({preventScroll: true});
        reader.scrollIntoView({block: 'start', behavior: 'instant'});
      }
      if (restoreProgress) restore(restoreProgress);
      else if (persist) saveProgress();
    }
    function close() {
      if (disposed) return;
      saveProgress();
      cancelRestore();
      finishTurn();
      stopPreview(current);
      stage.replaceChildren();
      current = null;
      index = -1;
      reader.hidden = true;
      root.classList.remove('is-open');
      choices.forEach(choice => choice.removeAttribute('aria-current'));
      if (status) status.textContent = '';
      const url = new URL(window.location.href);
      url.searchParams.delete('story');
      url.searchParams.delete('resume');
      url.hash = '';
      window.history.replaceState(window.history.state, '', url);
      openButton.focus({preventScroll: true});
      root.scrollIntoView({block: 'start', behavior: motion.matches ? 'instant' : 'smooth'});
    }
    function dispose() {
      if (disposed) return;
      saveProgress();
      cancelRestore();
      disposed = true;
      clearTimeout(saveTimer);
      finishTurn();
      stopPreview(current);
      cleanups.forEach(cleanup => cleanup());
      stage.replaceChildren();
      reader.hidden = true;
      root.classList.remove('is-open');
      openButton.hidden = true;
      choices.forEach(choice => { choice.hidden = true; choice.removeAttribute('aria-current'); });
      sizeControls.forEach(button => { button.hidden = true; });
      if (resume) resume.hidden = true;
      instances.delete(root);
    }
    const controls = '[data-farazine-open],[data-farazine-close],[data-farazine-previous],[data-farazine-next],[data-farazine-select],[data-farazine-resume],[data-farazine-size]';
    listen(root, 'click', event => {
      const control = event.target.closest(controls);
      if (!control || !root.contains(control) || control.disabled) return;
      if (!root.isConnected) { dispose(); return; }
      if (control.hasAttribute('data-farazine-size')) {
        const record = position();
        applySize(progress?.setSize(control.dataset.farazineSize) || control.dataset.farazineSize);
        if (record) restore(record);
        return;
      }
      if (control === resume) {
        const record = saved();
        if (record) select(stories.findIndex(story => story.dataset.storyHandle === record.story), {focus: true, restoreProgress: record});
        return;
      }
      if (control.hasAttribute('data-farazine-close')) { close(); return; }
      if (control === openButton) select(coverIndex, {focus: true});
      else if (control === previous) select(index - 1, {focus: true});
      else if (control === next) select(index + 1, {focus: true});
      else select(Number(control.dataset.farazineSelect), {focus: true});
    });
    listen(root, 'keydown', event => {
      if (event.defaultPrevented || event.ctrlKey || event.altKey || event.metaKey || event.shiftKey || event.target.isContentEditable || event.target.closest('input,select,textarea,[contenteditable]')) return;
      if (reader.hidden || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      if (!event.target.closest('[data-farazine-previous],[data-farazine-next],[data-farazine-read]') || event.target.closest('a,button,audio,video')) {
        if (event.target !== previous && event.target !== next) return;
      }
      event.preventDefault();
      select(index + (event.key === 'ArrowLeft' ? -1 : 1));
    });
    listen(root, 'shopify:block:select', event => {
      const block = event.target.closest('[data-farazine-block]');
      if (!block || !root.contains(block)) return;
      finishTurn();
      select(Number(block.dataset.farazineBlock), {focus: true, updateUrl: false});
    });
    listen(stage, 'pointerdown', event => {
      pointer = null;
      if (event.pointerType !== 'touch' || event.isPrimary === false || leaving || event.target.closest('a,button,input,select,textarea,audio,video')) return;
      const surface = event.target.closest('[data-farazine-swipe]');
      if (surface) pointer = {id: event.pointerId, x: event.clientX, y: event.clientY, surface};
    });
    listen(stage, 'pointerup', event => {
      const start = pointer;
      pointer = null;
      if (!start || event.pointerId !== start.id || !start.surface.contains(event.target)) return;
      const x = event.clientX - start.x, y = event.clientY - start.y;
      if (Math.abs(x) >= 70 && Math.abs(y) <= 35) select(index + (x < 0 ? 1 : -1));
    });
    listen(stage, 'pointercancel', () => { pointer = null; });
    listen(motion, 'change', event => { if (event.matches) finishTurn(); });
    listen(document, 'farazine:progress', refreshResume);
    const onScroll = event => {
      if (!current || disposed || restoring) return;
      if (!narrow.matches && event.target !== current.querySelector('[data-farazine-read]')) return;
      clearTimeout(saveTimer);
      saveTimer = setTimeout(saveProgress, 250);
    };
    stage.addEventListener('scroll', onScroll, true);
    cleanups.push(() => stage.removeEventListener('scroll', onScroll, true));
    listen(window, 'scroll', onScroll);
    const wrapper = document.querySelector('.page-wrapper');
    if (wrapper) listen(wrapper, 'scroll', onScroll);
    applySize(progress?.getSize() || 'regular');
    refreshResume();
    openButton.hidden = false;
    choices.forEach(choice => { choice.hidden = false; });
    const requested = new URL(window.location.href).searchParams.get('story');
    if (requested) {
      const requestedIndex = stories.findIndex(story => story.dataset.storyHandle === requested);
      const record = saved();
      const resumeIntent = new URL(window.location.href).searchParams.get('resume') === '1';
      if (requestedIndex >= 0) select(requestedIndex, {updateUrl: false, persist: false, restoreProgress: resumeIntent && record?.story === requested ? record : null});
      if (resumeIntent) {
        const url = new URL(window.location.href);
        url.searchParams.delete('resume');
        window.history.replaceState(window.history.state, '', url);
      }
    }
    return {dispose};
  }

  function prepare() {
    for (const [root, controller] of instances) if (!root.isConnected) controller.dispose();
    document.querySelectorAll('[data-farazine]').forEach(root => {
      if (!root.isConnected || instances.has(root)) return;
      const controller = mount(root);
      if (controller) instances.set(root, controller);
    });
  }
  document.addEventListener('shopify:section:load', prepare);
  document.addEventListener('shopify:section:unload', event => {
    for (const [root, controller] of instances) if (event.target === root || event.target.contains?.(root)) controller.dispose();
  });
  window.addEventListener('fara:before-navigate', () => { for (const controller of instances.values()) controller.dispose(); });
  window.addEventListener('pagehide', () => { for (const controller of instances.values()) controller.dispose(); });
  window.addEventListener('pageshow', prepare);
  window.FarazineIssue = {prepare};
  prepare();
})();
